import { useCallback, useEffect, useState } from 'react';
import { rbacService } from '../services/rbacService';
import { EMPTY_GRANTS } from '../utils/rbacConstants';
import { mergeGrantsWithCatalog } from '../utils/rbacMappers';
import { applyNodeGrantPatch } from '../utils/permissionTreeUtils';
import { syncBusinessFunctionsObjectPolicies } from '../utils/businessFunctionsPermissionUtils';
import {
  applyObjectGrantSideEffects,
  normalizeObjectGrantSideEffects,
} from '../utils/objectPermissionRules';
import {
  applyFeatureGrantSideEffects,
  normalizeFeatureGrantSideEffects,
} from '../utils/featurePermissionRules';

export default function useProfilePermissions(profileId) {
  const [catalog, setCatalog] = useState(null);
  const [profile, setProfile] = useState(null);
  const [grants, setGrants] = useState(EMPTY_GRANTS);
  const [resourceTree, setResourceTree] = useState({ accounts: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isResourceTreeLoading, setIsResourceTreeLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const load = useCallback(async () => {
    if (!profileId) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const [catalogRes, profileRes] = await Promise.all([
        rbacService.getPermissionCatalog(),
        rbacService.getProfileById(profileId),
      ]);

      if (!catalogRes.ok || !profileRes.ok) {
        setError('Unable to load permissions.');
        setCatalog(null);
        setProfile(null);
        setGrants(EMPTY_GRANTS);
        return;
      }

      const mergedGrants = mergeGrantsWithCatalog(
        catalogRes.data,
        profileRes.data?.permissions || EMPTY_GRANTS
      );

      setCatalog(catalogRes.data);
      setProfile(profileRes.data);
      setGrants(mergedGrants);
      setIsDirty(false);

      setIsResourceTreeLoading(true);
      const organisationId =
        typeof profileRes.data?.organisation === 'object'
          ? profileRes.data.organisation?.organisation_id ?? profileRes.data.organisation?.id
          : profileRes.data?.organisation;
      const treeRes = await rbacService.getResourceTree({
        organisationId,
      });
      if (treeRes.ok) {
        setResourceTree(treeRes.data || { accounts: [] });
      } else {
        setResourceTree({ accounts: [] });
      }
    } catch (err) {
      setError(err?.message || 'Unable to load permissions.');
    } finally {
      setIsResourceTreeLoading(false);
      setIsLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateFeatureGrant = useCallback((codename, granted) => {
    setGrants((prev) => applyFeatureGrantSideEffects(codename, granted, prev));
    setIsDirty(true);
  }, []);

  const updateObjectGrant = useCallback((model, action, granted) => {
    setGrants((prev) => applyObjectGrantSideEffects(model, action, granted, prev));
    setIsDirty(true);
  }, []);

  const updateNodeGrants = useCallback((node, granted) => {
    setGrants((prev) => applyNodeGrantPatch(prev, node, granted));
    setIsDirty(true);
  }, []);

  const updateResourceGrants = useCallback((resources) => {
    setGrants((prev) => syncBusinessFunctionsObjectPolicies({
      ...prev,
      resources: {
        accounts: resources?.accounts || [],
        campaigns: resources?.campaigns || [],
        form_fields: resources?.form_fields || [],
      },
    }));
    setIsDirty(true);
  }, []);

  const patchGrants = useCallback((nextGrants) => {
    setGrants((prev) => {
      const merged = {
        ...prev,
        features: nextGrants.features ?? prev.features,
        objects: nextGrants.objects ?? prev.objects,
        resources: nextGrants.resources ?? prev.resources,
        record_actions: nextGrants.record_actions ?? prev.record_actions,
      };
      return nextGrants.resources !== undefined
        ? syncBusinessFunctionsObjectPolicies(merged)
        : merged;
    });
    setIsDirty(true);
  }, []);

  const saveGrants = useCallback(async () => {
    if (!profileId) {
      return { ok: false, message: 'Profile not found.' };
    }

    setIsSaving(true);
    setError('');

    try {
      const syncedGrants = normalizeFeatureGrantSideEffects(
        normalizeObjectGrantSideEffects(syncBusinessFunctionsObjectPolicies(grants))
      );
      const res = await rbacService.updateProfileGrants(profileId, syncedGrants);
      if (!res.ok) {
        setError('Failed to save permissions.');
        return { ok: false, message: 'Failed to save permissions.' };
      }

      const savedGrants = mergeGrantsWithCatalog(
        catalog,
        res.data?.permissions || syncedGrants
      );
      setGrants(syncBusinessFunctionsObjectPolicies(savedGrants));
      setIsDirty(false);
      return { ok: true, data: savedGrants };
    } catch (err) {
      const message = err?.message || 'Failed to save permissions.';
      setError(message);
      return { ok: false, message };
    } finally {
      setIsSaving(false);
    }
  }, [catalog, grants, profileId]);

  return {
    catalog,
    profile,
    grants,
    resourceTree,
    isLoading,
    isResourceTreeLoading,
    isSaving,
    isDirty,
    error,
    load,
    saveGrants,
    updateFeatureGrant,
    updateObjectGrant,
    updateNodeGrants,
    updateResourceGrants,
    patchGrants,
  };
}
